import React from "react";
import { Box, Typography } from "@mui/material";
import { colors, mono, cardShadow } from "../theme/tokens";

const yesterdayItems = ["Reviewed onboarding flow", "Synced with design team"];
const todayItems = ["Draft Q3 roadmap", "1:1 with Sam"];

const longDate = (d: Date) =>
  d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

const shortDate = (d: Date) =>
  d
    .toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })
    .toUpperCase();

const Column: React.FC<{ label: string; items: string[] }> = ({
  label,
  items,
}) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: colors.primary,
        }}
      />
      <Typography
        sx={{
          fontFamily: mono,
          fontSize: "11px",
          fontWeight: 700,
          color: colors.primary,
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Typography>
    </Box>
    <Box
      sx={{
        borderTop: `1px solid ${colors.line}`,
        pt: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
      }}
    >
      {items.map((item) => (
        <Typography
          key={item}
          sx={{ fontSize: "13px", color: colors.bodyStrong }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  </Box>
);

/** The "daily summary for standup" card shown in the mindful task management section. */
const DailySummaryMockup: React.FC = () => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  return (
    <Box
      sx={{
        width: "100%",
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: "20px",
        p: 3.5,
        boxShadow: cardShadow,
      }}
    >
      <Typography
        sx={{
          fontFamily: mono,
          fontSize: "11px",
          letterSpacing: "0.06em",
          color: colors.faint,
          textTransform: "uppercase",
          mb: 1.25,
        }}
      >
        {longDate(today)}
      </Typography>

      <Box sx={{ mb: 2.25 }}>
        <Typography
          component="span"
          sx={{ fontSize: "22px", fontWeight: 800, color: colors.ink }}
        >
          Daily summary
        </Typography>
        <Typography
          component="span"
          sx={{ color: colors.faint, fontSize: "14px", ml: 1 }}
        >
          for standup
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 2.75 }}>
        <Box
          component="span"
          sx={{
            background: colors.primaryTint,
            color: colors.primary,
            fontSize: "12px",
            fontWeight: 600,
            px: 1.5,
            py: 0.75,
            borderRadius: "999px",
          }}
        >
          All projects
        </Box>
        <Box
          component="span"
          sx={{
            background: "#F1F1F4",
            color: colors.muted,
            fontSize: "12px",
            fontWeight: 600,
            px: 1.5,
            py: 0.75,
            borderRadius: "999px",
          }}
        >
          Website
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2.5,
          mb: 2.75,
        }}
      >
        <Column label={shortDate(yesterday)} items={yesterdayItems} />
        <Column label="TODAY, PLANNED" items={todayItems} />
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.75,
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            background: "#F1F1F4",
            borderRadius: "10px",
            p: "3px",
            gap: "2px",
          }}
        >
          {["Casual", "Formal", "Detailed"].map((tone, i) => (
            <Box
              key={tone}
              component="span"
              sx={{
                background: i === 0 ? colors.surface : "transparent",
                color: i === 0 ? colors.ink : colors.faint,
                fontSize: "12px",
                fontWeight: 600,
                px: 1.5,
                py: 0.75,
                borderRadius: "8px",
                boxShadow: i === 0 ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              }}
            >
              {tone}
            </Box>
          ))}
        </Box>
        <Box
          component="span"
          sx={{
            background: colors.primary,
            color: "#fff",
            fontSize: "12px",
            fontWeight: 600,
            px: 2,
            py: 1,
            borderRadius: "8px",
          }}
        >
          Copy
        </Box>
      </Box>

      <Box
        sx={{
          borderLeft: `3px solid ${colors.primary}`,
          background: colors.page,
          px: 2,
          py: 1.75,
          borderRadius: "0 8px 8px 0",
        }}
      >
        <Typography
          sx={{
            fontFamily: mono,
            fontSize: "12.5px",
            lineHeight: 1.6,
            color: colors.body,
            m: 0,
          }}
        >
          Wrapped up the onboarding flow review and synced with design. Today
          I'm focused on drafting the Q3 roadmap and prepping for my 1:1.
        </Typography>
      </Box>
    </Box>
  );
};

export default DailySummaryMockup;
